import { z } from 'zod'

/**
 * List state that lives in the URL.
 *
 * Every list view — jobs, clients, invoices, the dispatch board — serialises
 * its filters, sort and page into search params, so any view a person is
 * looking at can be pasted into a message and opened by someone else exactly
 * as they saw it. That only works if one definition drives both the client
 * that writes the URL and the server that reads it, which is why the codec
 * lives here rather than in either.
 *
 * Two properties matter and are tested:
 *
 * - **Defaults are omitted.** A URL carrying every default is unreadable and
 *   makes two identical views look different. `?status=SCHEDULED` beats
 *   `?page=1&perPage=50&sort=createdAt&dir=desc&status=SCHEDULED`.
 * - **Round-trip is stable.** Decoding then re-encoding produces the same
 *   string, so the address bar does not churn as a component re-renders.
 */

export const sortDirection = z.enum(['asc', 'desc'])
export type SortDirection = z.infer<typeof sortDirection>

export const MAX_PER_PAGE = 200

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1).default(1),
  perPage: z.coerce.number().int().min(1).max(MAX_PER_PAGE).catch(50).default(50),
})

/** Filters shared by every list. */
const baseListSchema = paginationSchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  dir: sortDirection.catch('desc').default('desc'),
  /** Include soft-deleted rows. Off by default; the recycle bin turns it on. */
  deleted: z.coerce.boolean().catch(false).default(false),
})

export const jobListSchema = baseListSchema.extend({
  /**
   * `dueAt` used to be offered here and is gone: it is set on 0 of 68,359 jobs,
   * so sorting by it ordered nothing. `.catch` means a link that still asks for
   * it falls back to `createdAt` rather than failing.
   *
   * `lastVisitAt` replaces it as the date the list can usefully order by — next
   * booking first on the Work Order tab, longest-neglected first on Follow-up.
   */
  sort: z
    .enum(['createdAt', 'number', 'lastVisitAt', 'status', 'totalIncTax'])
    .catch('createdAt')
    .default('createdAt'),
  status: z
    .array(
      z.enum([
        'LEAD',
        'QUOTING',
        'QUOTED',
        'APPROVED',
        'SCHEDULED',
        'IN_PROGRESS',
        'ON_HOLD',
        'AWAITING_PARTS',
        'COMPLETED',
        'CANCELLED',
      ]),
    )
    .default([]),
  priority: z.array(z.enum(['LOW', 'NORMAL', 'HIGH', 'EMERGENCY'])).default([]),
  /**
   * Which pipeline tab the Jobs list is showing. This is the primary filter now
   * — it replaces the four-stage picker — so it decides which `JobStatus`
   * values the query asks for.
   *
   * Defaults to `WORK_ORDER`, because live work is what the office opens the
   * list to see. `status` below still works alongside it, so links written
   * before the tabs existed keep resolving.
   */
  tab: z.enum(['QUOTE', 'WORK_ORDER', 'COMPLETED']).catch('WORK_ORDER').default('WORK_ORDER'),
  /**
   * The derived sub-status chip, narrowing the tab to one worklist. Absent
   * means the whole tab. Expressed as column predicates by `worklistWhere`, so
   * unlike `group` this one does reach the database — the counts and the
   * pagination have to be per status for the chip to mean anything.
   */
  worklist: z
    .enum([
      'DRAFT',
      'PRICED',
      'AWAITING_REPLY',
      'CLOSED_LOST',
      'UNSCHEDULED',
      'SCHEDULED',
      'FOLLOW_UP',
      'INVOICE_PENDING',
      'AWAITING_PAYMENT',
      'PAID',
    ])
    .optional(),
  /**
   * How the list is grouped on screen. Presentation only — the client groups
   * the page it already has, and the API ignores this.
   *
   * It is declared here anyway because the codec below is schema-driven: a
   * param this schema does not know about would be dropped from the URL the
   * next time any filter changed, so grouping would silently reset.
   */
  group: z.enum(['none', 'stage', 'client', 'assignee', 'due']).catch('none').default('none'),
  assigneeId: z.array(z.string()).default([]),
  clientId: z.string().optional(),
  siteId: z.string().optional(),
  categoryId: z.string().optional(),
  queueId: z.string().optional(),
  branchId: z.string().optional(),
  dueFrom: z.string().optional(),
  dueTo: z.string().optional(),
})
export type JobListParams = z.infer<typeof jobListSchema>

export const clientListSchema = baseListSchema.extend({
  sort: z.enum(['name', 'number', 'createdAt']).catch('name').default('name'),
  dir: sortDirection.catch('asc').default('asc'),
  type: z.array(z.enum(['RESIDENTIAL', 'COMMERCIAL', 'STRATA', 'GOVERNMENT', 'REAL_ESTATE'])).default([]),
  onCreditHold: z.coerce.boolean().optional(),
  archived: z.coerce.boolean().catch(false).default(false),
})
export type ClientListParams = z.infer<typeof clientListSchema>

export const invoiceListSchema = baseListSchema.extend({
  sort: z.enum(['issuedAt', 'dueAt', 'number', 'totalIncTax']).catch('issuedAt').default('issuedAt'),
  status: z
    .array(z.enum(['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'VOID', 'WRITTEN_OFF']))
    .default([]),
  clientId: z.string().optional(),
  /** Issued, past due and not settled. A one-click filter, not a status. */
  overdueOnly: z.coerce.boolean().catch(false).default(false),
  issuedFrom: z.string().optional(),
  issuedTo: z.string().optional(),
})
export type InvoiceListParams = z.infer<typeof invoiceListSchema>

export const quoteListSchema = baseListSchema.extend({
  sort: z.enum(['createdAt', 'sentAt', 'number', 'totalIncTax']).catch('createdAt').default('createdAt'),
  status: z
    .array(z.enum(['DRAFT', 'SENT', 'VIEWED', 'ACCEPTED', 'DECLINED', 'EXPIRED', 'SUPERSEDED']))
    .default([]),
  jobId: z.string().optional(),
})
export type QuoteListParams = z.infer<typeof quoteListSchema>

/** What `typeId` holds to ask for items with no type yet. */
export const MATERIAL_TYPE_NONE = 'none'

export const materialListSchema = baseListSchema.extend({
  sort: z.enum(['name', 'code', 'type', 'createdAt']).catch('name').default('name'),
  dir: sortDirection.catch('asc').default('asc'),
  /**
   * `kept` is our catalog, the items the line picker offers; `sm8` is the
   * ServiceM8 pool they are chosen from. ADR 022.
   */
  view: z.enum(['kept', 'sm8']).catch('kept').default('kept'),
  /** A `MaterialType` id, or `MATERIAL_TYPE_NONE` for items with no type. */
  typeId: z.string().min(1).max(64).optional(),
  supplierId: z.string().optional(),
  trackStock: z.coerce.boolean().optional(),
})
export type MaterialListParams = z.infer<typeof materialListSchema>

export const inboxListSchema = baseListSchema.extend({
  sort: z.enum(['receivedAt']).catch('receivedAt').default('receivedAt'),
  status: z.array(z.enum(['UNREAD', 'READ', 'SNOOZED', 'ARCHIVED', 'SPAM'])).default([]),
  channel: z.array(z.enum(['EMAIL', 'SMS', 'MMS', 'WEB_FORM', 'PHONE', 'PORTAL'])).default([]),
  assigneeId: z.string().optional(),
  /** Messages not yet turned into or attached to a job. */
  unlinkedOnly: z.coerce.boolean().catch(false).default(false),
  /**
   * Which message the reading pane is showing. Presentation only — the API
   * ignores it — but it is declared here for the same reason `group` is on the
   * jobs schema: the URL codec is schema-driven, so a param this schema does
   * not know about would be dropped the next time any filter changed, and the
   * open message would close itself. See decision 33.
   */
  selected: z.string().optional(),
})
export type InboxListParams = z.infer<typeof inboxListSchema>

/**
 * Timeline zoom factors, as URL strings so encode/decode stay exact on
 * `1.5` (a number would round-trip as `1.5` anyway, but enum membership is
 * clearer than a coerce-and-refine dance).
 */
export const boardZoomSchema = z.enum(['1', '1.5', '2', '3'])
export type BoardZoom = z.infer<typeof boardZoomSchema>

/** The dispatch board is a date window plus which staff rows are showing. */
/** What the board's map can plot: the window's bookings, the rail's queue, and parked jobs. */
export const MAP_KINDS = ['booked', 'unscheduled', 'parked'] as const
export type MapKind = (typeof MAP_KINDS)[number]
const MAP_DEFAULT_SHOW: readonly MapKind[] = ['booked', 'unscheduled']

export const MAP_GROUPS = ['none', 'staff', 'area', 'suburb', 'category'] as const
export type MapGroupBy = (typeof MAP_GROUPS)[number]

export const boardParamsSchema = z.object({
  /** ISO date, the first day shown. */
  date: z.string().optional(),
  /**
   * Which of the four boards is showing. In the URL so "look at Tuesday on the
   * map" is a link someone can send, rather than a set of instructions.
   *
   * Timeline is the one the board opens on: it is the view dispatch actually
   * works in, because a row per person across the day is where a gap and a
   * clash are visible at a glance. The day board is the exception, not the
   * landing, so `?view=day` carries in a link and timeline never has to.
   */
  view: z.enum(['day', 'timeline', 'week', 'map']).catch('timeline').default('timeline'),
  span: z.enum(['day', '3day', 'week']).catch('week').default('week'),
  /**
   * How far the timeline is zoomed past fitting the day to the window. `1`
   * fills the available width; higher values widen bookings and scroll.
   */
  zoom: boardZoomSchema.catch('1').default('1'),
  /**
   * Staff rows the dispatcher has tucked away. Everyone else stays on the
   * board; there is no "include only these people" filter anymore.
   */
  hiddenUserId: z.array(z.string()).default([]),
  /** When on, hidden rows come back (dimmed) so they can be restored. */
  showHidden: z.coerce.boolean().catch(false).default(false),
  /**
   * Board sections the dispatcher has shut.
   *
   * Also saved as a per-user preference, which is the default; this carries it
   * in a shared link so the recipient sees the board as the sender had it.
   */
  collapsed: z.array(z.string()).default([]),
  branchId: z.string().optional(),
  /** Hide staff with nothing booked in the window. */
  busyOnly: z.coerce.boolean().catch(false).default(false),
  /**
   * The job whose popup is open, so a shared link opens on it.
   *
   * Read by `JobOverlay` rather than by the board: this is the same param that
   * opens a job on every other screen, and clicking a booking resolves the
   * visit to its job here. There was a `visitId` beside it, which said which
   * block had been clicked; nothing needed it once the card was the only thing
   * a click produced. Decision 61.
   */
  jobId: z.string().optional(),
  /**
   * Find-on-board. Fades the bookings that do not match rather than filtering
   * them out, and never reaches the server: it searches the window already
   * loaded, which is what makes it instant and what limits what it can answer.
   */
  q: z.string().optional(),
  /** Pulse / chip filter over the day already loaded. */
  filter: z
    .enum(['everything', 'outNow', 'runningLate', 'commercial', 'notGoingToPlan'])
    .catch('everything')
    .default('everything'),
  /** How booking blocks are tinted. `area` is the service area of the job's suburb (ADR 021). */
  colourBy: z.enum(['status', 'category', 'area']).catch('status').default('status'),
  /**
   * Draw the hatched travel-estimate stripes on the day and timeline boards.
   * Off unless asked for. Drive-time totals in the pulse stay either way; this
   * only puts the bars on the grid.
   */
  showTravel: z.coerce.boolean().catch(false).default(false),
  /**
   * What the map plots. Never empty: the default comes back when the list
   * is, so the filter refuses to untick the last kind rather than showing a
   * map that silently ignored the click.
   */
  mapShow: z
    .array(z.enum(MAP_KINDS))
    .min(1)
    .catch([...MAP_DEFAULT_SHOW])
    .default([...MAP_DEFAULT_SHOW]),
  /** Staff on the map, by user id; `none` is a booking with nobody on it. Empty is everyone. */
  mapStaff: z.array(z.string()).default([]),
  /** Service areas on the map, by id; `none` is outside every area. Empty is everywhere. */
  mapArea: z.array(z.string()).default([]),
  /** How the map's run sheet is sectioned. */
  mapGroup: z.enum(MAP_GROUPS).catch('none').default('none'),
})
export type BoardParams = z.infer<typeof boardParamsSchema>

type AnyRecord = Record<string, unknown>

/**
 * Reads search params through a schema.
 *
 * Repeated keys (`?status=LEAD&status=QUOTED`) become arrays, which is the
 * convention every browser and server already agrees on, rather than a
 * comma-joined string that breaks the first time a value contains a comma.
 *
 * Unparseable values fall back to the default rather than throwing. A URL is
 * user input that arrives by way of copy-paste and hand-editing; a truncated
 * link should open the list, not an error page.
 */
export function decodeParams<TSchema extends z.ZodType>(
  schema: TSchema,
  search: URLSearchParams | string,
): z.infer<TSchema> {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search
  const shape = getShape(schema)
  const raw: AnyRecord = {}

  for (const key of new Set(params.keys())) {
    const values = params.getAll(key).filter((value) => value !== '')
    if (values.length === 0) continue

    raw[key] = isArrayField(shape?.[key]) ? values : values[0]
  }

  const result = schema.safeParse(raw)
  return result.success ? result.data : schema.parse({})
}

/**
 * Writes state back to search params, omitting anything left at its default.
 */
export function encodeParams<TSchema extends z.ZodType>(
  schema: TSchema,
  value: z.infer<TSchema>,
): URLSearchParams {
  const defaults = schema.parse({}) as AnyRecord
  const current = value as AnyRecord
  const params = new URLSearchParams()

  for (const key of Object.keys(current).sort()) {
    const item = current[key]
    if (item === undefined || item === null || item === '') continue

    if (Array.isArray(item)) {
      const fallback = defaults[key]
      if (Array.isArray(fallback) && sameArray(item, fallback)) continue
      for (const entry of [...item].sort()) params.append(key, String(entry))
      continue
    }

    if (defaults[key] === item) continue
    params.set(key, String(item))
  }

  return params
}

/** `encodeParams` as a string, with the `?` only when there is something to show. */
export function encodeSearch<TSchema extends z.ZodType>(
  schema: TSchema,
  value: z.infer<TSchema>,
): string {
  const encoded = encodeParams(schema, value).toString()
  return encoded === '' ? '' : `?${encoded}`
}

/**
 * The param names a schema owns.
 *
 * Everything else in a URL belongs to somebody else — the open job popup rides
 * on every screen as `?jobId=`, whatever list the screen happens to be — and a
 * filter change must leave it alone rather than encode the world from one
 * schema. `useUrlState` in the client is the caller.
 */
export function paramKeys<TSchema extends z.ZodType>(schema: TSchema): string[] {
  return Object.keys(getShape(schema) ?? {})
}

function sameArray(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length !== b.length) return false
  const left = [...a].map(String).sort()
  const right = [...b].map(String).sort()
  return left.every((entry, index) => entry === right[index])
}

function getShape(schema: z.ZodType): Record<string, z.ZodType> | undefined {
  const def = (schema as unknown as { def?: { shape?: Record<string, z.ZodType> } }).def
  return def?.shape
}

/** Unwraps defaults, optionals and catches to find out if a field is an array. */
function isArrayField(schema: z.ZodType | undefined): boolean {
  let current: unknown = schema

  for (let depth = 0; depth < 10 && current; depth += 1) {
    const def = (current as { def?: { type?: string; innerType?: unknown } }).def
    if (!def) return false
    if (def.type === 'array') return true
    if (!def.innerType) return false
    current = def.innerType
  }

  return false
}
